import React, { useState, useEffect } from 'react';
import { Quote, DEFAULT_QUOTES } from '../../utils/welcomePopupDefaults';
import './WelcomePopupManagement.css';

const QUOTES_STORAGE_KEY = 'welcome_popup_quotes';
const ENABLED_STORAGE_KEY = 'welcome_popup_enabled';

const WelcomePopupManagement: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuote, setNewQuote] = useState<Omit<Quote, 'id'>>({ text: '', author: '' });
  const [saveMessage, setSaveMessage] = useState<string>('');

  useEffect(() => {
    loadQuotes();
    loadEnabledState();
  }, []);

  const loadQuotes = () => {
    try {
      const savedQuotes = localStorage.getItem(QUOTES_STORAGE_KEY);
      if (savedQuotes) {
        const parsedQuotes = JSON.parse(savedQuotes);
        setQuotes(parsedQuotes);
      } else {
        // Initialize with default quotes if none exist
        setQuotes(DEFAULT_QUOTES);
        localStorage.setItem(QUOTES_STORAGE_KEY, JSON.stringify(DEFAULT_QUOTES));
      }
    } catch (error) {
      console.error('Error loading quotes:', error);
      setQuotes(DEFAULT_QUOTES);
    }
  };

  const loadEnabledState = () => {
    try {
      const savedEnabled = localStorage.getItem(ENABLED_STORAGE_KEY);
      if (savedEnabled !== null) {
        setIsEnabled(savedEnabled === 'true');
      } else {
        // Default to enabled
        setIsEnabled(true);
        localStorage.setItem(ENABLED_STORAGE_KEY, 'true');
      }
    } catch (error) {
      console.error('Error loading enabled state:', error);
      setIsEnabled(true);
    }
  };

  const saveQuotes = (quotesToSave: Quote[]) => {
    try {
      localStorage.setItem(QUOTES_STORAGE_KEY, JSON.stringify(quotesToSave));
      setQuotes(quotesToSave);
      
      // Dispatch event for real-time updates
      window.dispatchEvent(new CustomEvent('welcome_popup_quotes_updated', { detail: quotesToSave }));
      window.dispatchEvent(new StorageEvent('storage', {
        key: QUOTES_STORAGE_KEY,
        newValue: JSON.stringify(quotesToSave)
      }));
      
      setSaveMessage('Quotes saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving quotes:', error);
      setSaveMessage('Error saving quotes. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const saveEnabledState = (enabled: boolean) => {
    try {
      localStorage.setItem(ENABLED_STORAGE_KEY, enabled.toString());
      setIsEnabled(enabled);
      
      // Dispatch event for real-time updates
      window.dispatchEvent(new CustomEvent('welcome_popup_enabled_updated', { detail: enabled }));
      window.dispatchEvent(new StorageEvent('storage', {
        key: ENABLED_STORAGE_KEY,
        newValue: enabled.toString()
      }));
      
      setSaveMessage(enabled ? 'Popup enabled!' : 'Popup disabled!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving enabled state:', error);
      setSaveMessage('Error saving settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleAddQuote = () => {
    if (!newQuote.text.trim()) {
      setSaveMessage('Quote text is required.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    const quote: Quote = {
      id: Date.now().toString(),
      text: newQuote.text.trim(),
      author: newQuote.author?.trim() || undefined
    };

    saveQuotes([...quotes, quote]);
    setNewQuote({ text: '', author: '' });
    setShowAddForm(false);
  };

  const handleEditQuote = (quote: Quote) => {
    setEditingQuote(quote);
    setNewQuote({ text: quote.text, author: quote.author || '' });
    setShowAddForm(true);
  };

  const handleUpdateQuote = () => {
    if (!editingQuote || !newQuote.text.trim()) {
      setSaveMessage('Quote text is required.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    const updatedQuote: Quote = {
      id: editingQuote.id,
      text: newQuote.text.trim(),
      author: newQuote.author?.trim() || undefined
    };

    const updatedQuotes = quotes.map(q => q.id === editingQuote.id ? updatedQuote : q);
    saveQuotes(updatedQuotes);
    setEditingQuote(null);
    setNewQuote({ text: '', author: '' });
    setShowAddForm(false);
  };

  const handleDeleteQuote = (quoteId: string) => {
    if (window.confirm('Are you sure you want to delete this quote?')) {
      const updatedQuotes = quotes.filter(q => q.id !== quoteId);
      saveQuotes(updatedQuotes);
    }
  };

  const handleLoadDefaults = () => {
    if (window.confirm('This will replace all existing quotes with the default set. Are you sure?')) {
      saveQuotes(DEFAULT_QUOTES);
    }
  };

  const handleMoveQuote = (quoteId: string, direction: 'up' | 'down') => {
    const index = quotes.findIndex(q => q.id === quoteId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= quotes.length) return;

    const updatedQuotes = [...quotes];
    [updatedQuotes[index], updatedQuotes[newIndex]] = [updatedQuotes[newIndex], updatedQuotes[index]];
    saveQuotes(updatedQuotes);
  };

  const handleToggleEnabled = () => {
    saveEnabledState(!isEnabled);
  };

  const cancelEdit = () => {
    setEditingQuote(null);
    setNewQuote({ text: '', author: '' });
    setShowAddForm(false);
  };

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>Welcome popup</h2>
        <p>Manage the inspirational greeting that appears the first time a visitor opens the homepage</p>
      </div>

      <div className="section-content">
        {/* Enable/Disable Toggle */}
        <div className="welcome-popup-toggle-section">
          <div className="toggle-group">
            <label htmlFor="popup-enabled" className="toggle-label">
              <span className="toggle-label-text">Enable Welcome Popup</span>
              <span className="toggle-description">
                When enabled, a visitor sees a personalized greeting and an inspirational quote the first time they open the homepage. It stays closed after that in the same browser.
              </span>
            </label>
            <div className="toggle-switch-container">
              <input
                type="checkbox"
                id="popup-enabled"
                checked={isEnabled}
                onChange={handleToggleEnabled}
                className="toggle-switch-input"
              />
              <label htmlFor="popup-enabled" className="toggle-switch-label">
                <span className="toggle-switch-slider"></span>
              </label>
            </div>
          </div>
        </div>

        {/* Save Message */}
        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="quote-form-card">
            <h3>{editingQuote ? 'Edit Quote' : 'Add New Quote'}</h3>
            <div className="form-group">
              <label htmlFor="quote-text">Quote Text *</label>
              <textarea
                id="quote-text"
                value={newQuote.text}
                onChange={(e) => setNewQuote({ ...newQuote, text: e.target.value })}
                rows={4}
                className="form-textarea"
                placeholder="Enter the inspirational quote..."
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="quote-author">Author (Optional)</label>
              <input
                type="text"
                id="quote-author"
                value={newQuote.author}
                onChange={(e) => setNewQuote({ ...newQuote, author: e.target.value })}
                className="form-input"
                placeholder="e.g., Buddha, Rumi, Ansel Adams..."
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                onClick={editingQuote ? handleUpdateQuote : handleAddQuote}
                className="btn btn-primary"
              >
                <i className="fas fa-save"></i>
                {editingQuote ? 'Update Quote' : 'Add Quote'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="quotes-actions-bar">
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="btn btn-primary"
            >
              <i className="fas fa-plus"></i>
              Add New Quote
            </button>
          )}
          <button
            onClick={handleLoadDefaults}
            className="btn btn-secondary"
          >
            <i className="fas fa-redo"></i>
            Load Default Quotes
          </button>
        </div>

        {/* Quotes List */}
        <div className="quotes-list-container">
          <h3>Quotes ({quotes.length})</h3>
          {quotes.length === 0 ? (
            <div className="empty-state">
              <p>No quotes yet. Add your first quote or load default quotes.</p>
            </div>
          ) : (
            <div className="quotes-list">
              {quotes.map((quote, index) => (
                <div key={quote.id} className="quote-card">
                  <div className="quote-content">
                    <div className="quote-text">"{quote.text}"</div>
                    {quote.author && (
                      <div className="quote-author">— {quote.author}</div>
                    )}
                  </div>
                  <div className="quote-actions">
                    <div className="quote-move-buttons">
                      <button
                        onClick={() => handleMoveQuote(quote.id, 'up')}
                        disabled={index === 0}
                        className="btn-icon"
                        title="Move up"
                        aria-label="Move quote up"
                      >
                        <i className="fas fa-chevron-up"></i>
                      </button>
                      <button
                        onClick={() => handleMoveQuote(quote.id, 'down')}
                        disabled={index === quotes.length - 1}
                        className="btn-icon"
                        title="Move down"
                        aria-label="Move quote down"
                      >
                        <i className="fas fa-chevron-down"></i>
                      </button>
                    </div>
                    <button
                      onClick={() => handleEditQuote(quote)}
                      className="btn-icon btn-edit"
                      title="Edit quote"
                      aria-label="Edit quote"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteQuote(quote.id)}
                      className="btn-icon btn-delete"
                      title="Delete quote"
                      aria-label="Delete quote"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WelcomePopupManagement;
